import { useCallback, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ICONS } from './lib/icons';
import { mediaUrl, type PersonRef } from './lib/api';
import { dc } from './lib/style';
import { ToastCtx, useMe, type ToastFn } from './hooks';
import { chipOrder, DISCIPLINES, hueOf, initials } from './lib/domain';
import { useBarTitle } from './barTitle';

export function Icon({ name, sw = 1.6, size }: { name: string; sw?: number; size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={size ? { width: size, height: size } : undefined}
      dangerouslySetInnerHTML={{ __html: ICONS[name] || ICONS.other }}
    />
  );
}

export function DiscIcon({ disc, size = '', title }: { disc: string; size?: '' | 'sm' | 'xs' | 'lg'; title?: string }) {
  return (
    <span className={`disc-ico ${size}`} style={dc(disc)} title={title}>
      <Icon name={disc} sw={size === 'lg' ? 1.5 : 1.6} />
    </span>
  );
}

export function Avatar({
  person,
  size = '',
  me = false,
}: {
  person: Pick<PersonRef, 'name' | 'photo' | 'former'> & { id: number | null };
  size?: '' | 'md' | 'lg';
  me?: boolean;
}) {
  const cls = `av ${size}${me ? ' me' : ''}`;
  if (person.former)
    return (
      <span className={`${cls} av-former`} title="Ehemaliges Mitglied">
        –
      </span>
    );
  const src = mediaUrl(person.photo);
  if (src) return <img className={cls} src={src} alt="" title={person.name} loading="lazy" decoding="async" />;
  return (
    <span className={cls} style={{ '--h': hueOf(person.id ?? 0) } as CSSProperties} title={person.name}>
      {initials(person.name)}
    </span>
  );
}

// ---- Toasts with optional undo ----
export function ToastProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ msg: string; undo?: () => void; show: boolean }>({ msg: '', show: false });
  const timer = useRef<number | undefined>(undefined);
  const toast = useCallback<ToastFn>((msg, undo) => {
    setState({ msg, undo, show: true });
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setState(s => ({ ...s, show: false })), undo ? 6000 : 3000);
  }, []);
  return (
    <ToastCtx.Provider value={toast}>
      {children}
      <div className={`toast${state.show ? ' show' : ''}`} role="status" aria-live="polite">
        <span>{state.msg}</span>
        {state.undo && (
          <button
            onClick={() => {
              state.undo?.();
              setState(s => ({ ...s, show: false }));
            }}
          >
            Rückgängig
          </button>
        )}
      </div>
    </ToastCtx.Provider>
  );
}

/**
 * Page title: one line (no line above it – the app bar and the tabs already say where you are).
 * Phones show it in the app bar instead (`bar`, default: the title text); the heading here stays for screen readers.
 * `keep`: the heading stays visible on phones too (calendar: the month with its arrows belongs to the content).
 */
export function PageHead({
  title,
  right,
  className,
  bar,
  keep,
}: {
  title: ReactNode;
  right?: ReactNode;
  className?: string;
  bar?: string;
  keep?: boolean;
}) {
  const barTitle = bar ?? (typeof title === 'string' ? title : undefined);
  useBarTitle(barTitle);
  const cls = ['ph', className, barTitle && !keep ? 'ph-bar' : null].filter(Boolean).join(' ');
  return (
    <div className={cls}>
      <div>
        <h1 className="h-display">{title}</h1>
        <div className="dots" />
      </div>
      {right}
    </div>
  );
}
export const Loading = ({ text = 'Lädt …' }: { text?: string }) => (
  <div className="empty">
    <b>{text}</b>
  </div>
);
export const ErrorBox = ({ error }: { error: unknown }) => (
  <div className="empty">
    <b>Fehler</b>
    <p>{error instanceof Error ? error.message : 'Unbekannter Fehler'}</p>
  </div>
);

/** Discipline filter chips (several can be active). */
export function DiscChips({ value, onChange }: { value: Set<string>; onChange: (next: Set<string>) => void }) {
  const { data: me } = useMe();
  // the own top sports first – fixed while the page is open (a sign-up meanwhile does not move the chips)
  const [{ order, top }] = useState(() => chipOrder(me?.sport_counts));
  return (
    <div className="chips" aria-label="Disziplinen">
      {order.map((k, i) => (
        <button
          key={k}
          className={i === top - 1 ? 'chip chip-top-last' : 'chip'}
          data-disc={k}
          style={dc(k)}
          aria-pressed={value.has(k)}
          onClick={() => {
            const n = new Set(value);
            if (n.has(k)) n.delete(k);
            else n.add(k);
            onChange(n);
          }}
        >
          <i />
          {DISCIPLINES[k]}
        </button>
      ))}
    </div>
  );
}

/** An on/off filter as a chip in the chip row (e.g. "Nur meine", "Ohne Verkaufte") */
export function ToggleChip({
  pressed,
  onChange,
  icon,
  children,
}: {
  pressed: boolean;
  onChange: (next: boolean) => void;
  icon?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className="chip chip-toggle"
      style={{ ['--dc' as string]: 'var(--navy)' }}
      aria-pressed={pressed}
      onClick={() => onChange(!pressed)}
    >
      {icon ? <Icon name={icon} sw={1.8} /> : <i />}
      {children}
    </button>
  );
}

/** A small dropdown in the count line ("23 Termine · Kommend ▾") for sorting or the time range */
export function CountSelect<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: [T, string][];
  onChange: (v: T) => void;
}) {
  return (
    <select className="count-select" aria-label={label} value={value} onChange={e => onChange(e.target.value as T)}>
      {options.map(([v, l]) => (
        <option key={v} value={v}>
          {l}
        </option>
      ))}
    </select>
  );
}

/**
 * Close button of a detail page: goes BACK when the page was opened from within the app (the list keeps its scroll
 * position), otherwise – opened from a mail or a bookmark – to the given list.
 */
export function CloseButton({ to, label }: { to: string; label: string }) {
  const nav = useNavigate();
  return (
    <button
      type="button"
      className="icon-btn close"
      aria-label={label}
      onClick={() => {
        const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
        if (idx > 0) void nav(-1);
        else void nav(to);
      }}
    >
      <Icon name="x" sw={2} />
    </button>
  );
}
